import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type Paginated } from '@/shared/api/client';
import type { SiteObject, PhotoPolicy } from '@/shared/api/types';

export interface ObjectFilters {
  q?: string;
  customerId?: string;
  activeOnly?: boolean;
}

export function useObjects(filters: ObjectFilters) {
  const params = new URLSearchParams({ limit: '100' });
  if (filters.q) params.set('q', filters.q);
  if (filters.customerId) params.set('customerId', filters.customerId);
  if (filters.activeOnly) params.set('activeOnly', 'true');
  return useQuery({
    queryKey: ['objects', filters],
    queryFn: () => api<Paginated<SiteObject>>(`/api/objects?${params.toString()}`),
  });
}

export interface CreateObjectInput {
  customerId: string;
  name: string;
  address?: string;
  accessNotes?: string;
  photoPolicy?: PhotoPolicy;
}

export function useCreateObject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateObjectInput) =>
      api<SiteObject>('/api/objects', { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['objects'] }),
  });
}

// customerId ist beim Update nicht erlaubt (Backend OmitType).
export type UpdateObjectInput = Partial<Omit<CreateObjectInput, 'customerId'>> & {
  active?: boolean;
};

export function useUpdateObject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateObjectInput }) =>
      api<SiteObject>(`/api/objects/${id}`, { method: 'PATCH', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['objects'] }),
  });
}
